/**
 * Back-to-top control.
 *
 * Appears once the reader is a screen and a half down and returns them to the
 * top of the document. It sits out of the way of the cookie banner — that banner
 * owns the bottom-right corner until a choice is recorded — and honours a
 * reduced-motion preference by jumping rather than gliding.
 */
import React, { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { useContent } from '../i18n/LocaleProvider';

const THRESHOLD = 700;

export default function ScrollToTop() {
  const { UI } = useContent();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toTop = () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, left: 0, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <button
      type="button"
      className={`scroll-top${visible ? ' scroll-top--in' : ''}`}
      onClick={toTop}
      aria-label={UI.common.scrollToTop}
      title={UI.common.scrollToTop}
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      data-testid="scroll-to-top"
    >
      <ArrowUp size={19} strokeWidth={2.2} aria-hidden="true" />
    </button>
  );
}
